import * as IoC from 'typescript-ioc';
import {
    IApiClient,
    IApiResponse,
    IFileRequestOptions,
    IFileResponse,
    IRequestOptions,
    IResponseBase,
} from '../api-client';
import { catchRetryableApi5xxError, delayedRetryApi5xxError } from './api-client-method-decorators';

class ApiClientRetryHandleDecorator extends IApiClient {
    constructor(
        @IoC.Inject private readonly innerClient: IApiClient,
    ) {
        super();
    }

    public delete<TResponse, TRequest = {}>(url: string, parameters: TRequest, options?: IRequestOptions<TResponse>): Promise<IApiResponse<TResponse>> {
        return this.handleRetry(() => this.innerClient.delete(url, parameters, options), options ? options.maxRetryAttempts : 0);
    }

    public get<TResponse, TRequest = {}>(url: string, parameters: TRequest, options?: IRequestOptions<TResponse>): Promise<IApiResponse<TResponse>> {
        return this.handleRetry(() => this.innerClient.get(url, parameters, options), options ? options.maxRetryAttempts : 0);
    }

    public post<TResponse, TRequest = {}>(url: string, payload: TRequest, options?: IRequestOptions<TResponse>): Promise<IApiResponse<TResponse>> {
        // Non-idempotent calls can opt out of the automatic 5xx retry to avoid duplicate side effects.
        if (options?.disableRetry) {
            return this.innerClient.post(url, payload, options);
        }
        return this.handleRetry(() => this.innerClient.post(url, payload, options), options ? options.maxRetryAttempts : 0);
    }

    public put<TResponse, TRequest = {}>(url: string, payload: TRequest, options?: IRequestOptions<TResponse>): Promise<IApiResponse<TResponse>> {
        return this.handleRetry(() => this.innerClient.put(url, payload, options), options ? options.maxRetryAttempts : 0);
    }

    public patch<TResponse, TRequest = {}>(url: string, payload: TRequest, options?: IRequestOptions<TResponse>): Promise<IApiResponse<TResponse>> {
        return this.handleRetry(() => this.innerClient.patch(url, payload, options), options ? options.maxRetryAttempts : 0);
    }

    public getFile<TRequest = {}>(url: string, parameters?: TRequest, options?: IFileRequestOptions): Promise<IFileResponse> {
        return this.handleRetry(
            () => this.innerClient.getFile(url, parameters, options),
            options ? options.maxRetryAttempts : 0,
        );
    }

    @catchRetryableApi5xxError
    @delayedRetryApi5xxError([ 500, 1000, 2000 ])
    private async handleRetry<T extends IResponseBase>(apiCallFunction: () => Promise<T>, attemptsLeft: number) {
        try {
            return await apiCallFunction();
        } catch (e) {
            if (attemptsLeft > 0) {
                return this.handleRetry(apiCallFunction, attemptsLeft - 1);
            } else {
                throw e;
            }
        }
    }
}

export { ApiClientRetryHandleDecorator };
